import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Configuration } from '@app/models/config.model';
import { getAppFlow, routesAccessibleWithoutTenant } from '@app/v2/shared/utils';
import { CommonConstants } from '@core/common-constants';
import { environment } from '@environments/environment';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action } from '@ngrx/store';
import {
  closeTarget,
  ControlPageFacade,
  FeatureFlagsFacade,
  getUserPreferences,
  IEulaConfig,
  initializeTarget,
  navigateToError,
  openWebTechDialog,
  updateHarnessOnNoTenantSelected,
  UserDataFacade,
} from '@ra-state';
import { ActionButtonStyles, NotificationType } from '@ra-web-tech-ui-toolkit/components';
import { ConfigService } from '@rockwell-automation-inc/service';
import { CommandRequestBuilderService } from '@servicesV2/command-request-builder.service';
import { TenantEvent, UserEvent } from '@servicesV2/command-request.service';
import { ApiEndPoints, DataService } from '@servicesV2/data.service';
import { HarnessService, SERVICE_FLAGS_HARNESS } from '@servicesV2/harness.service';
import { LoggerService } from '@servicesV2/logger.service';
import * as R from 'remeda';
import { combineLatest, from, fromEvent, Observable, of, throwError, timer } from 'rxjs';
import { catchError, delay, filter, map, mergeMap, switchMap, tap, throttleTime } from 'rxjs/operators';
import { ApplicationCard, FlagValue, ICatalogInfo, ICommandResponse, toUserId } from '../lemans-app.model';
import {
  noLastAccessedTenant,
  setPersonalTenantContext,
  setTenantEffectiveRoles,
  setTenantId,
  setUserId,
} from '../user-data/user-data.action';
import { TENANT_MGMT_ROUTES } from '../user-data/user.model';
import {
  acceptEULA,
  acceptEULASuccess,
  baseURLSuccess,
  checkUserSession,
  extendSession,
  getApplications,
  getEnabledApplications,
  getEulaConfig,
  initAppRoute,
  logoutSession,
  navigateTo,
  recordUserActivity,
  resetSessionTimedOut,
  setAppIntialRoute,
  setErrorContext,
  setEulaConfigSuccess,
  setExtendSession,
  setLogoutSession,
  setProductCatalogSuccess,
  setSessionTimedOut,
  setUserActive,
  setVersions,
  startAppInitializer,
  userLoginComplete,
} from './control-page.actions';

@Injectable({
  providedIn: 'root',
})
export class ControlPageEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly commandRequestBuilder = inject(CommandRequestBuilderService);
  private readonly router = inject(Router);
  private readonly controlPageFacade = inject(ControlPageFacade);
  private readonly configService = inject<ConfigService<Configuration>>(ConfigService);
  private readonly dataService = inject(DataService);
  private readonly logger = inject(LoggerService);
  private readonly harnessService = inject(HarnessService);
  private readonly featureFlagsFacade = inject(FeatureFlagsFacade);
  private readonly userDataFacade = inject(UserDataFacade);

  getTimer: () => Observable<number>;

  routerSnapshot$ = this.controlPageFacade.routeStoreState$;

  initializeApp$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(startAppInitializer),
      tap(() => {
        try {
          this.harnessService.initializeTarget({
            identifier: 'common-service-portal',
            attributes: {
              Type: 'Application',
            },
          });
        } catch (error) {
          this.logger.error('Failed to initialize harness target', error);
        }
      }),
      switchMap(() => {
        this.configService.setConfig(environment.appConfiguration);
        this.dataService.initializeEndpoints();
        return [baseURLSuccess({ value: true })];
      }),
    );
  });

  ngrxOnInitEffects(): Action {
    this.logger.log('Initializing effects');
    this.generateTimer();
    return startAppInitializer();
  }

  loginCompleted$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(userLoginComplete),
      tap(() => {
        try {
          this.serviceFlagsHarness.initializeTarget({
            identifier: 'common-service-portal',
            attributes: {
              Type: 'User',
            },
          });
        } catch (error) {
          this.logger.error('Failed to initialize service flags harness target', error);
        }
      }),
      switchMap(({ payload: userProfile }) => {
        const actions: Action[] = [];
        const userId = userProfile.userId.replaceAll('-', '');
        actions.push(
          setUserId({ payload: userId }),
          initializeTarget({
            payload: {
              identifier: toUserId(userId),
              attributes: {
                Type: 'User',
                Domain: CommonConstants.getEmailDomain(userProfile.email),
              },
            },
          }),
          getUserPreferences(),
          recordUserActivity(),
          checkUserSession(),
          closeTarget({ harnessIdentifier: 'common-service-portal', scope: 'Common-Service' }),
        );
        return actions;
      }),
    );
  });

  acceptEULA$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(acceptEULA),
      concatLatestFrom(() => [
        this.controlPageFacade.isUserFound$,
        this.controlPageFacade.eulaConfig$,
        this.routerSnapshot$,
      ]),
      switchMap(([payload, isUserFound, eulaConfig, routerSnapshot]) => {
        let request$: Observable<ICommandResponse<any>>;
        if (isUserFound === false) {
          const commadRequest = this.commandRequestBuilder
            .new(ApiEndPoints.Users, 'POST', TenantEvent.ServiceAddedV2)
            .withBody({ acceptedEulaVersion: eulaConfig?.eulaVersion });

          request$ = commadRequest.invoke$().pipe(
            catchError((error: unknown) => {
              this.router.navigate(['/error/user']);
              return throwError(() => error);
            }),
          );
        } else {
          const commadRequest = this.commandRequestBuilder
            .new(ApiEndPoints.Eula + eulaConfig?.eulaVersion + '/accept', 'POST', UserEvent.UserEulaAccepted)
            .withWaitOn200Ok();
          request$ = commadRequest.invoke$();
        }
        return combineLatest([request$, of(payload.value), of(routerSnapshot)]);
      }),
      switchMap(([resp, returnTo, routerSnapshot]) => {
        this.logger.log('RESP:    ', resp);
        const sameOriginReturnTo = returnTo ? this.getSameOriginUrl(returnTo) : undefined;

        if (sameOriginReturnTo) {
          this.hardRedirect(sameOriginReturnTo);
          return from([] as Action[]);
        }

        const actions: Action[] = [acceptEULASuccess()];
        const routerState: any = routerSnapshot;
        const path = routerState.queryParams.postEula || TENANT_MGMT_ROUTES.ORGANIZATIONS;
        actions.push(navigateTo({ path }));
        return from(actions);
      }),
    );
  });

  private getSameOriginUrl(returnTo: string): string | undefined {
    try {
      const url = new URL(returnTo, globalThis.location.origin);
      const isHttp = url.protocol === 'http:' || url.protocol === 'https:';
      return isHttp && url.origin === globalThis.location.origin ? url.href : undefined;
    } catch {
      return undefined;
    }
  }

  private hardRedirect(url: string): void {
    globalThis.location.replace(url);
  }

  userID$ = this.userDataFacade.userID$;
  routeSnapshot$ = this.controlPageFacade.routeStoreState$;

  initAppRoute$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(initAppRoute),
      concatLatestFrom(() => [this.routeSnapshot$, this.userID$]),
      switchMap(([appRoutingData, routeState, userId]) => {
        const actions: Action[] = [];
        const { accessibleTenants, eulaAcceptanceRequired, lastAccessedTenantId } = appRoutingData.payload;

        if (eulaAcceptanceRequired) {
          return actions;
        }

        const appFlow = getAppFlow(routeState.url);
        const bookmarkedOrgId = routeState['params']?.orgId;
        const isOrgIdInAccessibleTenants = accessibleTenants?.some((tenant) => tenant.id === bookmarkedOrgId);

        // Determine tenant context and navigation
        if (userId === lastAccessedTenantId) {
          actions.push(setPersonalTenantContext());
        } else if (bookmarkedOrgId && isOrgIdInAccessibleTenants && bookmarkedOrgId !== lastAccessedTenantId) {
          actions.push(setTenantId({ value: bookmarkedOrgId }));
        } else if (bookmarkedOrgId && !isOrgIdInAccessibleTenants && lastAccessedTenantId) {
          actions.push(
            openWebTechDialog({
              config: {
                title: 'Organization Switch Confirmation',
                message:
                  'The selected organization is not available in your list. You will be redirected to your last accessed organization.',
                messageType: NotificationType.Info,
                buttons: [
                  {
                    label: 'Ok',
                    buttonStyle: ActionButtonStyles.Main,
                    shouldAutofocus: true,
                  },
                ],
              },
            }),
            setTenantId({ value: String(lastAccessedTenantId) }),
            navigateTo({
              path: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${lastAccessedTenantId}/${routeState.url.split('/').pop()}`,
            }),
          );
        } else if (lastAccessedTenantId) {
          // No bookmarked orgId, use last accessed
          actions.push(setTenantId({ value: String(lastAccessedTenantId) }));
        } else {
          actions.push(noLastAccessedTenant(), updateHarnessOnNoTenantSelected());
        }

        // Always set the initial app route
        actions.push(setAppIntialRoute({ payload: { flow: appFlow, snapshot: routeState } }));

        return actions;
      }),
    );
  });

  defaultNavigation$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(setPersonalTenantContext, noLastAccessedTenant, setTenantEffectiveRoles),
      concatLatestFrom(() => [this.routeSnapshot$]),
      switchMap(([action, routeState]) => {
        const actions: Action[] = [];
        switch (action.type) {
          case setTenantEffectiveRoles.type:
            actions.push(navigateTo({ path: action.navigateTo || routeState.url }));
            break;
          case setPersonalTenantContext.type:
          case noLastAccessedTenant.type:
          default: {
            const appFlow = getAppFlow(routeState.url);
            if (!routesAccessibleWithoutTenant(appFlow)) {
              actions.push(navigateTo({ path: TENANT_MGMT_ROUTES.ORGANIZATIONS }));
            }
            break;
          }
        }
        return actions;
      }),
    );
  });

  navigateTo$ = createEffect(
    (): Observable<any> => {
      return this.actions$.pipe(
        ofType(navigateTo),
        tap((payload) => {
          // Use navigateByUrl which handles the complete URL string including query params
          return [this.router.navigateByUrl(payload.path)];
        }),
      );
    },
    { dispatch: false },
  );

  navigateToError$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(navigateToError),
      map((payload) => {
        this.router.navigate(['error']);
        return setErrorContext({ payload: payload.payload });
      }),
    );
  });

  getEulaConfig$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getEulaConfig),
      switchMap(() => {
        return this.dataService.getEulaConfig$();
      }),
      switchMap((eulaConfig: IEulaConfig) => {
        return [setEulaConfigSuccess({ payload: eulaConfig })];
      }),
    );
  });

  getProductCatalog$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(userLoginComplete),
      switchMap(() => {
        return this.dataService.getCatalogInfo$();
      }),
      switchMap((catalog: ICatalogInfo[]) => {
        return [setProductCatalogSuccess({ payload: catalog })];
      }),
    );
  });

  getCspUrls$ = createEffect((): Observable<ReturnType<typeof getApplications | typeof getEnabledApplications>> => {
    return this.actions$.pipe(
      ofType(userLoginComplete),
      switchMap(() => {
        return this.dataService.getCspUrls$();
      }),
      switchMap((cspUrls) => {
        const apps = this.configService.config.applications;
        const applications = apps.map((app) => {
          const appId = app.appId.toLowerCase();
          // Add safety check to match appId case insensitively
          const cspAppId = Object.keys(cspUrls).find((key) => key.toLowerCase() === appId);
          if (cspAppId && cspUrls[cspAppId]) {
            return {
              ...app,
              provisioningUrl: cspUrls[cspAppId],
            };
          }
          return app;
        });
        const appConfig = { ...environment.appConfiguration, applications };
        this.configService.setConfig(appConfig);

        const excludedCards: Observable<ApplicationCard[]>[] = [];
        const flaggedApps = applications.filter((app) => app.featureFlagName !== undefined);
        flaggedApps.forEach((app) => {
          if (app.featureFlagName !== undefined) {
            excludedCards.push(
              this.featureFlagsFacade.getFlagValue$(app.featureFlagName).pipe(
                map((value: FlagValue) => {
                  this.logger.debug('App dependent on flag', app.appId, app.featureFlagName, value);
                  return value ? [] : [app];
                }),
              ),
            );
          }
        });
        if (excludedCards.length === 0) {
          return of([]);
        } else {
          return combineLatest(excludedCards);
        }
      }),
      switchMap((appCards) => {
        const apps = this.configService.config.applications;
        const excludedCards = R.flat(appCards);
        const filteredCards = R.differenceWith(apps, excludedCards, (carda, cardb) => carda.appId === cardb.appId);
        const enabledApps = R.clone(filteredCards);
        return [getApplications({ payload: apps }), getEnabledApplications({ payload: enabledApps })];
      }),
    );
  });

  getVersions$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(userLoginComplete),
      switchMap(() => {
        return this.dataService.getVersions$();
      }),
      switchMap((apiVersion) => {
        return [setVersions({ value: apiVersion })];
      }),
    );
  });

  logoutSession$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(logoutSession),
      switchMap(() => [setLogoutSession({ value: true })]),
    );
  });

  extendSession$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(extendSession),
      switchMap(() => [resetSessionTimedOut(), setExtendSession({ value: true })]),
    );
  });

  recordUserActivity$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(recordUserActivity),
      delay(60000), // 1 Min Delay
      switchMap(() => from(['keydown', 'click', 'wheel', 'mousemove'])),
      mergeMap((event) => fromEvent(document, event)),
      throttleTime(600000), //Check after 10 minutes
      concatLatestFrom(() => [this.controlPageFacade.isUserActive$]),
      filter(([, isUserActive]) => !isUserActive),
      switchMap(() => [setUserActive({ value: true })]),
    );
  });

  defaultTimer = (): Observable<number> => {
    return timer(CommonConstants.idleTimeout, CommonConstants.idleTimeout);
  };

  generateTimer(getTimer = this.defaultTimer): void {
    this.getTimer = getTimer;
  }

  checkUserSession$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(checkUserSession),
      switchMap(() => this.getTimer()),
      concatLatestFrom(() => [this.controlPageFacade.sessionTimedOut$, this.controlPageFacade.isUserActive$]),
      filter(([, sessionTimedOut]) => !sessionTimedOut),
      switchMap(([, , isUserActive]) => {
        return isUserActive
          ? [setUserActive({ value: false }), extendSession()]
          : [setSessionTimedOut({ value: true })];
      }),
    );
  });

  resetSessionTimedOut$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(resetSessionTimedOut),
      switchMap(() => [setSessionTimedOut({ value: false })]),
    );
  });

  serviceFlagsHarness = inject(SERVICE_FLAGS_HARNESS);
  constructor() {
    this.logger.log('control page effects loads');
  }
}
